import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Layer} from '../../../../libs/shared/ui/src/components/layers/layer';
import {Anchor} from '../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {RepositionOverlay} from '../../../../libs/shared/ui/src/components/reposition/repositionOverlay';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {InfoBubbleDiv} from './infoBubbleCommon';

/*
 * Props.
 */

export interface InfoBubbleOverlayProps {
  /** Ref to the anchor, or bounds provider. */
  anchor: Anchor;
  /** Whether the overlay has a corresponding bubble. */
  hasPopover: boolean;
  /** If the overlay is interactive, pointer events will not pass through. */
  isInteractive?: boolean;
  /** Method to close the info bubble. Will be invoked when the overlay is clicked. */
  closeInfoBubble: Handler;
  /** If true, doesn't add any bubble styling, just highlights the anchor */
  noBubble: boolean;
}

/*
 * Style.
 */

interface OverlayStyleProps {
  $isInteractive: boolean;
}
const StyledOverlayDiv = styled.div<OverlayStyleProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: ${(p) => (p.$isInteractive ? 'auto' : 'inherit')};
`;

// If there is no popover, we highlight the target's exact dimensions.
interface HighlightStyleProps {
  $hasPopover: boolean;
  $noBubble: boolean;
}
const StyledHighlightDiv = styled(InfoBubbleDiv)<HighlightStyleProps>`
  position: absolute;
  ${(p) => computeHighlightPositionStyle(p)}

  border: 1px solid rgba(255, 255, 255, 0.4);
  ${(p) => boxShadow(p.theme, `0 0 0 1000em rgba(0, 0, 0, 0.2)`)};
  pointer-events: inherit;
`;

const computeHighlightPositionStyle = (props: HighlightStyleProps) => {
  if (props.$noBubble) {
    return css`
      inset: 0px;

      border-radius: 0px;
    `;
  }

  return css`
    inset: ${props.$hasPopover ? '-10px' : '-4px'};

    border-radius: 20px;
  `;
};

/*
 * Component.
 */

export const InfoBubbleOverlay: FC<InfoBubbleOverlayProps> = ({
  anchor,
  hasPopover,
  noBubble,
  isInteractive,
  closeInfoBubble,
}) => (
  <Layer>
    <StyledOverlayDiv $isInteractive={Boolean(isInteractive)} onClick={closeInfoBubble} />
    <RepositionOverlay anchor={anchor} isInteractive={isInteractive}>
      <StyledHighlightDiv $hasPopover={hasPopover} $noBubble={noBubble} />
    </RepositionOverlay>
  </Layer>
);
